import React, { useCallback, useEffect, useRef } from 'react'
import type { Guide } from '@/lib/guides/types/guide'
import type { DocumentProjection } from '@/lib/canvas/types/document-projection'
import { logger } from '@/utils/logger'
import {
  type BookSettingPayload,
  type UnitOfMeasureValue,
  editorGridModeFromBookPayload,
  getBookSettingAll,
  snapToGridFromBookPayload,
  snapToGuidesFromBookPayload,
  unitOfMeasureToRulerUnit,
} from '@/api/services/book-setting.service'
import { editorGuideSettingsService } from '@/api/services/editor-guide-settings.service'
import { rulerGuidesService } from '@/api/services/ruler-guides.service'
import { clampNudgePt, restorableSettingsService } from '@/api/services/restorable-settings.service'
import {
  type SnapSettingsLoaded,
  clampSnapDistancePt,
  snapSettingsService,
} from '@/api/services/snap-settings.service'
import {
  DEFAULT_VIEW_SETTINGS,
  type ViewSettingsDto,
  applyNewSpreadViewSettingsDefaults,
  gridModeToShowGridString,
  showGridStringToGridMode,
  viewSettingsService,
} from '@/api/services/view-settings.service'
import type { PreferencesValues } from '@/components/app/layout/preferences-dialog'
import { doColumnsRowsSettingsFitPage } from '@/lib/guides/columns-rows-fit'
import { resolveEffectiveGuideSettings } from '@/lib/guides/guide-settings-precedence'
import type { HomeDocumentLayout, HomeZoomAndSpread } from '../../types/home-groups'
import { MIN_COLUMNS_FALLBACK, MIN_ROWS_FALLBACK } from '../../constants/guide-settings'
import {
  getDefaultGuideSettingsFromBookPayload,
  getNextDocumentLayout,
} from '../../helpers/book-setting-layout'
import { persistGuideDefaultsToStorage } from '../../helpers/guide-defaults-storage'
import { getLatestTimestamp, shouldPreferBookSetting } from '../../helpers/setting-timestamps'

/**
 * Book and per-spread editor settings: loading book settings, spread guide settings and the
 * layout-fit check, fetching and reconciling per-spread view / snap / restorable settings (book wins
 * when newer), debounced ruler-guide saves, and the view / snap / grid toggles that persist them.
 */
export function useHomeEditorSettings(
  deps: Pick<HomeZoomAndSpread, 'isSpreadCheckpointLoaded' | 'spreadId'> &
    Pick<
      HomeDocumentLayout,
      | 'setViewSettings'
      | 'spreadViewSettings'
      | 'setSpreadViewSettings'
      | 'showGuides'
      | 'setShowGuides'
      | 'showSmartGuides'
      | 'setShowSmartGuides'
      | 'snapToGuides'
      | 'setSnapToGuides'
      | 'spreadSnapSettings'
      | 'setSpreadSnapSettings'
      | 'guidesPerSpread'
      | 'setGuidesPerSpread'
      | 'setRulerUnit'
      | 'setUnitOfMeasure'
      | 'setColumns'
      | 'setRows'
      | 'setColGutter'
      | 'setRowGutter'
      | 'setDefaultGuideGuttersLinked'
      | 'spreadGuideSettings'
      | 'setSpreadGuideSettings'
      | 'spreadGuideUpdatedAt'
      | 'setSpreadGuideUpdatedAt'
      | 'defaultGuideSettings'
      | 'setDefaultGuideSettings'
      | 'bookSettingsPayload'
      | 'setBookSettingsPayload'
      | 'hasLoadedBookSettings'
      | 'setHasLoadedBookSettings'
      | 'hasLoadedSpreadGuideSettings'
      | 'setHasLoadedSpreadGuideSettings'
      | 'loadedSpreadGuideSettingsFor'
      | 'setLoadedSpreadGuideSettingsFor'
      | 'setIsGuidesDialogOpen'
      | 'setIsResizeLayoutDialogOpen'
      | 'setDocumentLayout'
      | 'setTotalPages'
      | 'setPreferencesValues'
      | 'gridMode'
      | 'hasCheckedLayoutFitForSpreadRef'
      | 'snapGuideDistancePt'
      | 'resolvedLayout'
      | 'spreadProjection'
      | 'horizontalGuideAnchorX'
    >
) {
  const {
    isSpreadCheckpointLoaded,
    spreadId,
    setViewSettings,
    spreadViewSettings,
    setSpreadViewSettings,
    showGuides,
    setShowGuides,
    showSmartGuides,
    setShowSmartGuides,
    snapToGuides,
    setSnapToGuides,
    spreadSnapSettings,
    setSpreadSnapSettings,
    guidesPerSpread,
    setGuidesPerSpread,
    setRulerUnit,
    setUnitOfMeasure,
    setColumns,
    setRows,
    setColGutter,
    setRowGutter,
    setDefaultGuideGuttersLinked,
    spreadGuideSettings,
    setSpreadGuideSettings,
    spreadGuideUpdatedAt,
    setSpreadGuideUpdatedAt,
    defaultGuideSettings,
    setDefaultGuideSettings,
    bookSettingsPayload,
    setBookSettingsPayload,
    hasLoadedBookSettings,
    setHasLoadedBookSettings,
    hasLoadedSpreadGuideSettings,
    setHasLoadedSpreadGuideSettings,
    loadedSpreadGuideSettingsFor,
    setLoadedSpreadGuideSettingsFor,
    setIsGuidesDialogOpen,
    setIsResizeLayoutDialogOpen,
    setDocumentLayout,
    setTotalPages,
    setPreferencesValues,
    gridMode,
    hasCheckedLayoutFitForSpreadRef,
    snapGuideDistancePt,
    resolvedLayout,
    spreadProjection,
    horizontalGuideAnchorX,
  } = deps

  const applyBookSettingsPayload = useCallback(
    (payload: BookSettingPayload | null | undefined) => {
      if (payload) {
        setBookSettingsPayload(payload)
        setRulerUnit(unitOfMeasureToRulerUnit(payload.unitOfMeasure))
        setDefaultGuideGuttersLinked(
          typeof payload.linkedGutterValues === 'boolean' ? payload.linkedGutterValues : true
        )
        if (payload.unitOfMeasure) {
          setUnitOfMeasure(payload.unitOfMeasure as UnitOfMeasureValue)
        }

        setDefaultGuideSettings(getDefaultGuideSettingsFromBookPayload(payload))

        const pageCount =
          typeof payload.job?.pageCount === 'number' && payload.job.pageCount > 0
            ? payload.job.pageCount
            : 0

        setTotalPages(pageCount)

        setDocumentLayout((prev) => getNextDocumentLayout(payload, prev))
        return
      }

      setBookSettingsPayload(null)
      setRulerUnit('pica')
      setDefaultGuideGuttersLinked(true)
      setDefaultGuideSettings(null)
      setTotalPages(0)
    },
    [
      setBookSettingsPayload,
      setDefaultGuideGuttersLinked,
      setDefaultGuideSettings,
      setDocumentLayout,
      setRulerUnit,
      setTotalPages,
      setUnitOfMeasure,
    ]
  )

  const resetBookSettingsFallback = useCallback(() => {
    setBookSettingsPayload(null)
    setRulerUnit('pica')
    setDefaultGuideGuttersLinked(true)
    setDefaultGuideSettings(null)
  }, [setBookSettingsPayload, setDefaultGuideGuttersLinked, setDefaultGuideSettings, setRulerUnit])

  const refreshBookSettings = useCallback(async (): Promise<void> => {
    setHasLoadedBookSettings(false)

    try {
      const data = await getBookSettingAll()

      applyBookSettingsPayload(data?.payload)
    } catch {
      resetBookSettingsFallback()
    } finally {
      setHasLoadedBookSettings(true)
    }
  }, [applyBookSettingsPayload, resetBookSettingsFallback, setHasLoadedBookSettings])

  const refreshSpreadGuideSettings = useCallback(async (): Promise<void> => {
    setHasLoadedSpreadGuideSettings(false)
    setLoadedSpreadGuideSettingsFor('')

    if (!spreadId || !isSpreadCheckpointLoaded) {
      setSpreadGuideSettings(null)
      setSpreadGuideUpdatedAt({})
      setHasLoadedSpreadGuideSettings(true)
      setLoadedSpreadGuideSettingsFor('')
      return
    }

    const [columnResult, rowResult, rulerResult] = await Promise.allSettled([
      editorGuideSettingsService.getColumnGuides(spreadId),
      editorGuideSettingsService.getRowGuides(spreadId),
      rulerGuidesService.getRulerGuides(spreadId, spreadProjection),
    ])

    const hasColumns = columnResult.status === 'fulfilled' && columnResult.value.count > 0
    const hasRows = rowResult.status === 'fulfilled' && rowResult.value.count > 0

    setSpreadGuideUpdatedAt({
      column: columnResult.status === 'fulfilled' ? columnResult.value.updatedAt : undefined,
      row: rowResult.status === 'fulfilled' ? rowResult.value.updatedAt : undefined,
    })

    if (hasColumns || hasRows) {
      setSpreadGuideSettings({
        columns: hasColumns
          ? columnResult.value.count
          : (defaultGuideSettings?.columns ?? MIN_COLUMNS_FALLBACK),
        rows: hasRows ? rowResult.value.count : (defaultGuideSettings?.rows ?? MIN_ROWS_FALLBACK),
        colGutter: hasColumns
          ? columnResult.value.gutterWidth
          : (defaultGuideSettings?.colGutter ?? 24),
        rowGutter: hasRows ? rowResult.value.gutterWidth : (defaultGuideSettings?.rowGutter ?? 24),
        hasColumns,
        hasRows,
      })
    }

    if (rulerResult.status === 'fulfilled') {
      guideSaveSkipRef.current = true
      setGuidesPerSpread((prev) => ({ ...prev, [spreadId]: rulerResult.value }))
    }

    setHasLoadedSpreadGuideSettings(true)
    setLoadedSpreadGuideSettingsFor(spreadId)
  }, [
    defaultGuideSettings,
    spreadId,
    isSpreadCheckpointLoaded,
    spreadProjection,
    setGuidesPerSpread,
    setHasLoadedSpreadGuideSettings,
    setLoadedSpreadGuideSettingsFor,
    setSpreadGuideSettings,
    setSpreadGuideUpdatedAt,
  ])

  const handleGuidesDialogOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setIsGuidesDialogOpen(false)
        return
      }

      void Promise.allSettled([refreshBookSettings(), refreshSpreadGuideSettings()]).finally(() => {
        setIsGuidesDialogOpen(true)
      })
    },
    [refreshBookSettings, refreshSpreadGuideSettings, setIsGuidesDialogOpen]
  )

  // Load ruler unit, unitOfMeasure, and trim size (spread dimensions) from bookSetting on mount
  useEffect(() => {
    let cancelled = false

    setHasLoadedBookSettings(false)

    getBookSettingAll()
      .then((data) => {
        if (cancelled) return

        const payload = data?.payload

        applyBookSettingsPayload(payload)

        setHasLoadedBookSettings(true)
      })
      .catch(() => {
        if (cancelled) return
        resetBookSettingsFallback()
        setHasLoadedBookSettings(true)
      })

    return () => {
      cancelled = true
    }
  }, [applyBookSettingsPayload, resetBookSettingsFallback, setHasLoadedBookSettings])

  useEffect(() => {
    void refreshSpreadGuideSettings()
  }, [refreshSpreadGuideSettings])

  const effectiveGuideSettings = React.useMemo(() => {
    return resolveEffectiveGuideSettings({
      defaultGuideSettings,
      spreadGuideSettings,
      planbookUpdatedAt: bookSettingsPayload?.planbookUpdatedAt,
      spreadUpdatedAt: getLatestTimestamp(spreadGuideUpdatedAt.column, spreadGuideUpdatedAt.row),
    })
  }, [
    bookSettingsPayload?.planbookUpdatedAt,
    defaultGuideSettings,
    spreadGuideSettings,
    spreadGuideUpdatedAt,
  ])

  useEffect(() => {
    if (!spreadId) return
    if (!hasLoadedBookSettings || !hasLoadedSpreadGuideSettings) return
    if (loadedSpreadGuideSettingsFor !== spreadId) return
    if (!effectiveGuideSettings) return

    setColumns(effectiveGuideSettings.columns)
    setRows(effectiveGuideSettings.rows)
    setColGutter(effectiveGuideSettings.colGutter)
    setRowGutter(effectiveGuideSettings.rowGutter)
  }, [
    effectiveGuideSettings,
    hasLoadedBookSettings,
    hasLoadedSpreadGuideSettings,
    loadedSpreadGuideSettingsFor,
    spreadId,
    setColGutter,
    setColumns,
    setRowGutter,
    setRows,
  ])

  useEffect(() => {
    hasCheckedLayoutFitForSpreadRef.current[spreadId] = false
    setIsResizeLayoutDialogOpen(false)
  }, [spreadId, hasCheckedLayoutFitForSpreadRef, setIsResizeLayoutDialogOpen])

  useEffect(() => {
    if (!hasLoadedBookSettings || !hasLoadedSpreadGuideSettings) return
    if (hasCheckedLayoutFitForSpreadRef.current[spreadId]) return

    hasCheckedLayoutFitForSpreadRef.current[spreadId] = true

    if (!effectiveGuideSettings) return

    if (
      !doColumnsRowsSettingsFitPage(
        effectiveGuideSettings,
        resolvedLayout.pageInnerWidth,
        resolvedLayout.pageInnerHeight
      )
    ) {
      setIsResizeLayoutDialogOpen(true)
    }
  }, [
    effectiveGuideSettings,
    hasLoadedBookSettings,
    hasLoadedSpreadGuideSettings,
    resolvedLayout.pageInnerHeight,
    resolvedLayout.pageInnerWidth,
    spreadId,
    hasCheckedLayoutFitForSpreadRef,
    setIsResizeLayoutDialogOpen,
  ])

  useEffect(() => {
    let cancelled = false

    setSpreadViewSettings(null)

    if (!spreadId || !isSpreadCheckpointLoaded) {
      return () => {
        cancelled = true
      }
    }

    void viewSettingsService.getViewSettings(spreadId).then((data) => {
      if (cancelled) return
      if (data) {
        const resolved = applyNewSpreadViewSettingsDefaults(data)

        setSpreadViewSettings(resolved)

        if (resolved !== data) {
          // WMPCM-10576: nil ViewSettings corrected to this spread's first explicit record —
          // persist it so it has a real `updatedAt` (and stays on) on the next load.
          void viewSettingsService.updateViewSettings(spreadId, resolved).catch((err) => {
            logger.warn('Failed to save default view settings for new spread:', err)
          })
        }
      } else {
        setViewSettings(DEFAULT_VIEW_SETTINGS)
      }
    })

    return () => {
      cancelled = true
    }
  }, [spreadId, isSpreadCheckpointLoaded, setSpreadViewSettings, setViewSettings])

  useEffect(() => {
    let cancelled = false

    setSpreadSnapSettings(null)

    if (!spreadId || !isSpreadCheckpointLoaded) {
      return () => {
        cancelled = true
      }
    }

    snapSettingsService.getSnapSettings(spreadId).then((data) => {
      if (cancelled || !data) return
      setSpreadSnapSettings(data)
    })

    return () => {
      cancelled = true
    }
  }, [spreadId, isSpreadCheckpointLoaded, setSpreadSnapSettings])

  // These refs let each effect distinguish "spread data changed" (full init needed) from
  // "only bookSettingsPayload got a new reference" (apply book-wins overrides only, no reset).
  // Effect 1 owns the first two; Effect 2 owns the third — separate so Effect 1 updating its
  // ref on the same flush doesn't prevent Effect 2 from detecting a spreadSnapSettings change.
  const prevViewEffectViewSettingsRef = useRef<ViewSettingsDto | null | undefined>(undefined)
  const prevViewEffectSnapSettingsRef = useRef<SnapSettingsLoaded | null | undefined>(undefined)
  const prevSnapEffectSnapSettingsRef = useRef<SnapSettingsLoaded | null | undefined>(undefined)

  useEffect(() => {
    const spreadChanged =
      spreadViewSettings !== prevViewEffectViewSettingsRef.current ||
      spreadSnapSettings !== prevViewEffectSnapSettingsRef.current

    prevViewEffectViewSettingsRef.current = spreadViewSettings
    prevViewEffectSnapSettingsRef.current = spreadSnapSettings

    const bookWinsOverViewSettings =
      !!bookSettingsPayload &&
      shouldPreferBookSetting(bookSettingsPayload.planbookUpdatedAt, spreadViewSettings?.updatedAt)

    const bookWinsOverSnapSettings =
      !!bookSettingsPayload &&
      shouldPreferBookSetting(bookSettingsPayload.planbookUpdatedAt, spreadSnapSettings?.updatedAt)

    if (spreadChanged) {
      // While the per-spread GET is still in flight (spreadViewSettings === null), fall back to
      // a loading placeholder with showBleed off rather than DEFAULT_VIEW_SETTINGS — otherwise the
      // bleed bar flashes on (DEFAULT_VIEW_SETTINGS.showBleed is true) before the real per-spread
      // value arrives and corrects it.
      const spreadSource = spreadViewSettings ?? { ...DEFAULT_VIEW_SETTINGS, showBleed: false }
      let nextView: ViewSettingsDto = { ...spreadSource }

      if (bookWinsOverViewSettings) {
        const nextGridMode = editorGridModeFromBookPayload(
          bookSettingsPayload,
          showGridStringToGridMode(nextView.showGrid)
        )

        nextView = {
          ...nextView,
          showGrid: gridModeToShowGridString(nextGridMode),
          snapToGrid: snapToGridFromBookPayload(bookSettingsPayload, nextView.snapToGrid),
        }
      }

      if (bookWinsOverViewSettings || bookWinsOverSnapSettings) {
        nextView = { ...nextView, showGuides: true }
      }

      setViewSettings(nextView)
      setShowGuides(nextView.showGuides)
      setShowSmartGuides(nextView.showSmartGuides)
      persistGuideDefaultsToStorage({
        showGuides: nextView.showGuides,
        showSmartGuides: nextView.showSmartGuides,
      })
    } else {
      // Only bookSettingsPayload changed — apply book-wins overrides on top of current live
      // state without resetting everything to the stale server snapshot.
      if (bookWinsOverViewSettings) {
        setViewSettings((prev) => {
          const nextGridMode = editorGridModeFromBookPayload(
            bookSettingsPayload!,
            showGridStringToGridMode(prev.showGrid)
          )

          return {
            ...prev,
            showGrid: gridModeToShowGridString(nextGridMode),
            snapToGrid: snapToGridFromBookPayload(bookSettingsPayload!, prev.snapToGrid),
          }
        })
      }

      if (bookWinsOverViewSettings || bookWinsOverSnapSettings) {
        setViewSettings((prev) => ({ ...prev, showGuides: true }))
        setShowGuides(true)
      }
    }
  }, [
    bookSettingsPayload,
    spreadViewSettings,
    spreadSnapSettings,
    setShowGuides,
    setShowSmartGuides,
    setViewSettings,
  ])

  useEffect(() => {
    const snapChanged = spreadSnapSettings !== prevSnapEffectSnapSettingsRef.current

    prevSnapEffectSnapSettingsRef.current = spreadSnapSettings

    const bookWinsOverSnap =
      bookSettingsPayload != null &&
      shouldPreferBookSetting(bookSettingsPayload.planbookUpdatedAt, spreadSnapSettings?.updatedAt)

    let nextSnapToGuides = spreadSnapSettings?.snapToGuides ?? false

    if (bookWinsOverSnap) {
      nextSnapToGuides = snapToGuidesFromBookPayload(bookSettingsPayload, nextSnapToGuides)
    }

    if (snapChanged) {
      setSnapToGuides(nextSnapToGuides)

      if (spreadSnapSettings) {
        setPreferencesValues((prev) => ({
          ...prev,
          snapDistance: spreadSnapSettings.snapDistance,
        }))
      }
    } else if (bookWinsOverSnap) {
      // Only book changed and book wins — apply snapToGuides override, leave snapDistance alone
      setSnapToGuides(nextSnapToGuides)
    }
  }, [bookSettingsPayload, spreadSnapSettings, setPreferencesValues, setSnapToGuides])

  useEffect(() => {
    let cancelled = false

    if (!spreadId || !isSpreadCheckpointLoaded) {
      return () => {
        cancelled = true
      }
    }

    restorableSettingsService.getRestorableSettings(spreadId).then((data) => {
      if (cancelled || !data) return
      setPreferencesValues((prev) => ({
        ...prev,
        nudge: data.nudge,
      }))
    })

    return () => {
      cancelled = true
    }
  }, [spreadId, isSpreadCheckpointLoaded, setPreferencesValues])

  // Persist ruler guides to the backend (debounced) whenever they change for the active spread
  const guideSaveSkipRef = useRef(true)
  const pendingGuideSaveRef = useRef<{
    spreadId: string
    guides: Guide[]
    projection: DocumentProjection
    horizontalGuideAnchorX: number
  } | null>(null)

  useEffect(() => {
    // Skip the first render and any render triggered by loading from the API
    if (guideSaveSkipRef.current) {
      guideSaveSkipRef.current = false
      pendingGuideSaveRef.current = null
      return
    }

    const currentGuides = guidesPerSpread[spreadId]

    if (currentGuides === undefined) return

    pendingGuideSaveRef.current = {
      spreadId,
      guides: currentGuides,
      projection: spreadProjection,
      horizontalGuideAnchorX,
    }

    const timer = setTimeout(() => {
      pendingGuideSaveRef.current = null
      rulerGuidesService
        .updateRulerGuides(spreadId, currentGuides, spreadProjection, horizontalGuideAnchorX)
        .catch((err) => {
          logger.warn('Failed to save ruler guides:', err)
        })
    }, 400)

    return () => {
      clearTimeout(timer)
    }
  }, [guidesPerSpread, spreadId, spreadProjection, horizontalGuideAnchorX])

  // Reset the skip flag when the spread changes so the initial load doesn't trigger a save.
  // Flush any pending ruler-guide save on spread switch or unmount so data isn't lost.
  useEffect(() => {
    guideSaveSkipRef.current = true

    return () => {
      const pending = pendingGuideSaveRef.current

      if (pending) {
        pendingGuideSaveRef.current = null
        rulerGuidesService
          .updateRulerGuides(
            pending.spreadId,
            pending.guides,
            pending.projection,
            pending.horizontalGuideAnchorX
          )
          .catch((err) => {
            logger.warn('Failed to save ruler guides:', err)
          })
      }
    }
  }, [spreadId])

  const persistViewSettingsPatch = useCallback(
    (patch: Partial<ViewSettingsDto>) => {
      setViewSettings((prev) => {
        const next = { ...prev, ...patch }

        void viewSettingsService.updateViewSettings(spreadId, next).catch((err) => {
          logger.warn('Failed to save view settings:', err)
        })
        return next
      })
    },
    [spreadId, setViewSettings]
  )

  const persistShowGuides = useCallback(
    (checked: boolean) => {
      setShowGuides(checked)
      persistGuideDefaultsToStorage({ showGuides: checked })
      persistViewSettingsPatch({ showGuides: checked })
      if (!checked) {
        setSnapToGuides(false)
        void snapSettingsService
          .updateSnapSettings(spreadId, {
            snapToGuides: false,
            snapDistance: snapGuideDistancePt,
          })
          .catch((err) => {
            logger.warn('Failed to save snap settings:', err)
          })
      }
    },
    [spreadId, snapGuideDistancePt, persistViewSettingsPatch, setShowGuides, setSnapToGuides]
  )

  const persistShowSmartGuides = useCallback(
    (checked: boolean) => {
      setShowSmartGuides(checked)
      persistGuideDefaultsToStorage({ showGuides, showSmartGuides: checked })
      persistViewSettingsPatch({ showSmartGuides: checked })
    },
    [showGuides, persistViewSettingsPatch, setShowSmartGuides]
  )

  const handleSnapToGuidesToggle = useCallback(
    (checked: boolean) => {
      setShowGuides(true)
      persistGuideDefaultsToStorage({ showGuides: true, showSmartGuides })
      persistViewSettingsPatch({ showGuides: true })
      setSnapToGuides(checked)
      void snapSettingsService
        .updateSnapSettings(spreadId, {
          snapToGuides: checked,
          snapDistance: snapGuideDistancePt,
        })
        .catch((err) => {
          logger.warn('Failed to save snap settings:', err)
        })
    },
    [
      showSmartGuides,
      spreadId,
      snapGuideDistancePt,
      persistViewSettingsPatch,
      setShowGuides,
      setSnapToGuides,
    ]
  )

  const handlePreferencesChange = useCallback(
    (next: PreferencesValues) => {
      setPreferencesValues(next)
      const dist = clampSnapDistancePt(parseInt(next.snapDistance.trim(), 10) || 0)

      void snapSettingsService
        .updateSnapSettings(spreadId, {
          snapDistance: dist,
          snapToGuides,
        })
        .catch((err) => {
          logger.warn('Failed to save snap settings:', err)
        })

      const nudge = clampNudgePt(parseInt(next.nudge.trim(), 10) || 0)

      void restorableSettingsService.updateRestorableSettings(spreadId, { nudge }).catch((err) => {
        logger.warn('Failed to save restorable settings (nudge):', err)
      })
    },
    [spreadId, snapToGuides, setPreferencesValues]
  )

  const handleSnapToGridToggle = useCallback(
    (checked: boolean) => {
      setViewSettings((prev) => {
        const next =
          checked && !prev.snapToGrid
            ? {
                ...prev,
                snapToGrid: true,
                ...(showGridStringToGridMode(prev.showGrid) === 'off'
                  ? { showGrid: gridModeToShowGridString('full') }
                  : {}),
              }
            : { ...prev, snapToGrid: checked }

        void viewSettingsService.updateViewSettings(spreadId, next).catch((err) => {
          logger.warn('Failed to save view settings:', err)
        })
        return next
      })
    },
    [spreadId, setViewSettings]
  )

  const handleToggleFullPica = useCallback(
    (checked: boolean) => {
      const next = checked ? 'full' : gridMode === 'full' ? 'off' : gridMode

      persistViewSettingsPatch({
        showGrid: gridModeToShowGridString(next),
        ...(checked ? { snapToGrid: true } : {}),
      })
    },
    [gridMode, persistViewSettingsPatch]
  )

  const handleToggleHalfPica = useCallback(
    (checked: boolean) => {
      const next = checked ? 'half' : gridMode === 'half' ? 'off' : gridMode

      persistViewSettingsPatch({
        showGrid: gridModeToShowGridString(next),
        ...(checked ? { snapToGrid: true } : {}),
      })
    },
    [gridMode, persistViewSettingsPatch]
  )

  const handleShowBleedBarToggle = useCallback(
    (checked: boolean) => {
      persistViewSettingsPatch({ showBleed: checked })
    },
    [persistViewSettingsPatch]
  )

  const handleShowTextLinkingToggle = useCallback(
    (checked: boolean) => {
      persistViewSettingsPatch({ showLink: checked })
    },
    [persistViewSettingsPatch]
  )

  const handleShowStickyNotesToggle = useCallback(
    (checked: boolean) => {
      persistViewSettingsPatch({ showSticky: checked })
    },
    [persistViewSettingsPatch]
  )

  return {
    applyBookSettingsPayload,
    resetBookSettingsFallback,
    refreshBookSettings,
    refreshSpreadGuideSettings,
    handleGuidesDialogOpenChange,
    effectiveGuideSettings,
    prevViewEffectViewSettingsRef,
    prevViewEffectSnapSettingsRef,
    prevSnapEffectSnapSettingsRef,
    guideSaveSkipRef,
    pendingGuideSaveRef,
    persistViewSettingsPatch,
    persistShowGuides,
    persistShowSmartGuides,
    handleSnapToGuidesToggle,
    handlePreferencesChange,
    handleSnapToGridToggle,
    handleToggleFullPica,
    handleToggleHalfPica,
    handleShowBleedBarToggle,
    handleShowTextLinkingToggle,
    handleShowStickyNotesToggle,
  }
}
